/* ==========================================================================
   STRYVE - product page controller v2 (sections/sv-product.liquid)
   - Gallery: one scroll-snap track at every size. Desktop: viewport-bounded stage driven by a
     vertical thumbnail rail, prev/next buttons and the arrow keys. Mobile: full-bleed swipe with
     counter + dots. Shopify video autoplays muted while in view; external video / 3D load on demand.
   - Variant picker driven by the product JSON: price, unit price, availability, media, bundle totals,
     ?variant= in the URL.
   - Volume tiers + mix & match (one select per unit, collapsed), quantity stepper fallback.
   - Add to bag via window.Stryve.cart.add (drawer) with native POST fallback.
   - Delivery estimate (business days), desktop fold guard (keeps Add to bag + Buy it now + delivery
     on the first screen), sticky mobile bar shown whenever the main button is off screen,
     animated accordions.
   Every instance is scoped to its section root and is idempotent; the theme editor's section
   load / unload events re-init / tear down.
   ========================================================================== */
(function () {
  'use strict';

  var W = window;
  var S = (W.Stryve = W.Stryve || {});
  if (S.__pdpScript) return;
  S.__pdpScript = true;

  var MQ_DESK = W.matchMedia('(min-width: 990px)');
  var MQ_RM = W.matchMedia('(prefers-reduced-motion: reduce)');
  var ADDED_MS = 2200;

  /* ------------------------------------------------------------------ utils */
  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function toInt(v, d) {
    var n = parseInt(v, 10);
    return isNaN(n) ? d : n;
  }

  function behavior() {
    return MQ_RM.matches ? 'auto' : 'smooth';
  }

  function onMq(mq, fn, sig) {
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', fn, sig);
    else if (typeof mq.addListener === 'function') mq.addListener(fn);
  }

  /* ------------------------------------------------------------------ money */
  var decoder = null;

  function decodeEntities(str) {
    if (str.indexOf('&') === -1) return str;
    decoder = decoder || document.createElement('textarea');
    decoder.innerHTML = str;
    return decoder.value;
  }

  function withDelimiters(cents, precision, thousands, decimal) {
    var num = (cents / 100).toFixed(precision);
    var parts = num.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    var fmt = format || S.moneyFormat || '${{amount}}';
    var out;
    if (W.Shopify && typeof W.Shopify.formatMoney === 'function') {
      try {
        out = W.Shopify.formatMoney(cents, fmt);
      } catch (e) {
        out = null;
      }
    }
    if (!out) {
      out = String(fmt).replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
        switch (key) {
          case 'amount_no_decimals':
            return withDelimiters(cents, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return withDelimiters(cents, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return withDelimiters(cents, 0, ' ', ',');
          case 'amount_with_space_separator':
            return withDelimiters(cents, 2, ' ', ',');
          case 'amount_with_period_and_space_separator':
            return withDelimiters(cents, 2, ' ', '.');
          default:
            return withDelimiters(cents, 2, ',', '.');
        }
      });
    }
    return decodeEntities(String(out).replace(/<[^>]*>/g, '')).trim();
  }

  /* Buyer sees another currency than the shop's (Markets): prices in the product JSON are in that
     currency, so shop.money_format would print the wrong symbol. The section then renders Shopify's own
     money output for 12,345.00 and JS copies its symbol / separators / decimals. */
  function parseMoneySample(sample) {
    if (!sample) return null;
    var s = decodeEntities(String(sample).replace(/<[^>]*>/g, '')).trim();
    var m = /12(\D?)345(?:(\D)(\d{1,3}))?/.exec(s);
    if (!m) return null;
    return {
      pre: s.slice(0, m.index),
      post: s.slice(m.index + m[0].length),
      t: m[1] || '',
      d: m[2] || '.',
      n: m[3] ? m[3].length : 0,
    };
  }

  function formatFromSample(cents, f) {
    var num = (Math.round(Number(cents) || 0) / 100).toFixed(f.n);
    var parts = num.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, f.t);
    return f.pre + whole + (parts[1] ? f.d + parts[1] : '') + f.post;
  }

  /* ------------------------------------------------------------ date utils */
  function addBusinessDays(from, n) {
    var d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
    }
    return d;
  }

  function fmtDate(d) {
    try {
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (e) {
      return d.toDateString();
    }
  }

  /* ============================================================== instance */
  function init(root) {
    if (!root || root.__svPdp) return;

    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    var sig = ac ? { signal: ac.signal } : false;
    var passive = ac ? { passive: true, signal: ac.signal } : { passive: true };
    var observers = [];
    var timers = [];
    var cleanupRaf = [];
    var listeners = []; // fallback cleanup when AbortController is unavailable

    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts || sig);
      if (!ac) listeners.push([el, type, fn, opts]);
    }

    function later(fn, ms) {
      var t = setTimeout(fn, ms);
      timers.push(t);
      return t;
    }

    var fmtAttr = root.getAttribute('data-money-format');
    if (fmtAttr && !S.moneyFormat) S.moneyFormat = fmtAttr;
    var moneyFmt = S.moneyFormat || fmtAttr;
    var sampleFmt = parseMoneySample(root.getAttribute('data-money-sample'));

    function money(cents) {
      return sampleFmt ? formatFromSample(cents, sampleFmt) : formatMoney(cents, moneyFmt);
    }

    var product = null;
    var jsonEl = qs('[data-sv-product-json]', root);
    if (jsonEl) {
      try {
        product = JSON.parse(jsonEl.textContent);
      } catch (e) {
        product = null;
      }
    }

    /* ---------------------------------------------------------- gallery */
    var gallery = qs('[data-sv-gallery]', root);
    var track = qs('[data-sv-track]', root);
    var countCur = qs('[data-sv-count-cur]', root);
    var dots = qsa('[data-sv-dot]', root);
    var railBox = qs('[data-sv-thumbs]', root);
    var rail = railBox ? railBox.parentNode : null;
    var thumbs = qsa('[data-sv-thumb]', root);
    var prevBtn = qs('[data-sv-prev]', root);
    var nextBtn = qs('[data-sv-next]', root);
    var slideIdx = -1;
    var scrollRaf = 0;
    var lockIdx = -1; // target of a programmatic move: indicators jump straight to it
    var lockTimer = 0;

    function slides() {
      return track ? qsa('[data-sv-slide]', track) : [];
    }

    function currentSlide() {
      var list = slides();
      if (!list.length) return 0;
      var x = Math.abs(track.scrollLeft);
      var best = 0;
      var bestDist = Infinity;
      var base = list[0].offsetLeft;
      list.forEach(function (s, i) {
        var dist = Math.abs(s.offsetLeft - base - x);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    // The slide about to be shown and its neighbours start loading now (they are lazy by default).
    function warm(i) {
      var list = slides();
      [i, i + 1, i - 1].forEach(function (k) {
        if (!list[k]) return;
        qsa('img[loading="lazy"]', list[k]).forEach(function (img) {
          img.setAttribute('loading', 'eager');
        });
      });
    }

    function paintRailEdges() {
      if (!rail || !railBox) return;
      var over = railBox.scrollHeight > railBox.clientHeight + 1;
      rail.classList.toggle('is-overflowing', over);
      rail.classList.toggle('is-end', over && railBox.scrollTop + railBox.clientHeight >= railBox.scrollHeight - 1);
    }

    function revealThumb(i) {
      var t = thumbs[i];
      if (!t || !railBox || !railBox.clientHeight) return;
      var top = t.offsetTop;
      var bottom = top + t.offsetHeight;
      var st = railBox.scrollTop;
      var h = railBox.clientHeight;
      var to = null;
      if (top < st + 8) to = Math.max(0, top - 8);
      else if (bottom > st + h - 8) to = bottom - h + 8;
      if (to === null) return;
      try {
        railBox.scrollTo({ top: to, behavior: behavior() });
      } catch (e) {
        railBox.scrollTop = to;
      }
    }

    // A nav button about to be disabled while focused hands focus to its partner (or the track).
    function setNavDisabled(btn, off, partner) {
      if (!btn || btn.disabled === off) return;
      var had = off && document.activeElement === btn;
      btn.disabled = off;
      if (!had) return;
      var to = partner && !partner.disabled ? partner : track;
      if (to) {
        if (to === track && !track.hasAttribute('tabindex')) track.setAttribute('tabindex', '-1');
        try {
          to.focus({ preventScroll: true });
        } catch (e) {}
      }
    }

    function paintAt(i, force) {
      if (i === slideIdx && !force) return;
      slideIdx = i;
      var n = slides().length;
      if (countCur) countCur.textContent = String(i + 1);
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
      });
      thumbs.forEach(function (t, k) {
        var cur = k === i;
        t.classList.toggle('is-active', cur);
        t.setAttribute('tabindex', cur ? '0' : '-1');
        if (cur) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      setNavDisabled(prevBtn, i <= 0, nextBtn);
      setNavDisabled(nextBtn, i >= n - 1, prevBtn);
      revealThumb(i);
      warm(i);
    }

    function paintIndicators(force) {
      scrollRaf = 0;
      if (!track) return;
      var i = currentSlide();
      if (lockIdx >= 0) {
        if (i !== lockIdx) return;
        lockIdx = -1;
      }
      paintAt(i, force === true);
    }

    function unlock() {
      if (lockIdx < 0) return;
      lockIdx = -1;
      if (!scrollRaf) scrollRaf = requestAnimationFrame(paintIndicators);
    }

    function goTo(i, instant) {
      var list = slides();
      if (!track || !list.length) return;
      i = Math.max(0, Math.min(list.length - 1, i));
      var left = list[i].offsetLeft - list[0].offsetLeft;
      warm(i);
      if (Math.abs(track.scrollLeft - left) > 1) {
        lockIdx = i;
        clearTimeout(lockTimer);
        lockTimer = later(unlock, 900);
      }
      paintAt(i);
      try {
        track.scrollTo({ left: left, behavior: instant ? 'auto' : behavior() });
      } catch (e) {
        track.scrollLeft = left;
      }
    }

    function activeIndex() {
      return lockIdx >= 0 ? lockIdx : slideIdx >= 0 ? slideIdx : currentSlide();
    }

    if (track) {
      on(track, 'scroll', function () {
        if (!scrollRaf) scrollRaf = requestAnimationFrame(paintIndicators);
      }, passive);
      // A shopper gesture always wins over a programmatic move still in flight.
      ['wheel', 'touchstart', 'pointerdown'].forEach(function (type) {
        on(track, type, unlock, passive);
      });
    }

    if (railBox) {
      on(railBox, 'scroll', paintRailEdges, passive);
    }

    thumbs.forEach(function (t) {
      on(t, 'click', function () {
        goTo(toInt(t.getAttribute('data-sv-thumb'), 0));
      });
    });

    if (prevBtn) {
      on(prevBtn, 'click', function () {
        goTo(activeIndex() - 1);
      });
    }
    if (nextBtn) {
      on(nextBtn, 'click', function () {
        goTo(activeIndex() + 1);
      });
    }

    // Arrow keys anywhere in the gallery (rail, stage, arrows); up/down also work inside the rail.
    if (gallery) {
      on(gallery, 'keydown', function (event) {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        var t = event.target;
        var tag = t && t.tagName ? t.tagName.toLowerCase() : '';
        if (tag === 'input' || tag === 'select' || tag === 'textarea' || tag === 'model-viewer') return;
        if (tag === 'video' && t.controls) return;
        var inRail = !!(railBox && railBox.contains(t));
        var k = event.key;
        var d = 0;
        if (k === 'ArrowRight' || (inRail && k === 'ArrowDown')) d = 1;
        else if (k === 'ArrowLeft' || (inRail && k === 'ArrowUp')) d = -1;
        if (!d) return;
        event.preventDefault();
        var n = Math.max(0, Math.min(slides().length - 1, activeIndex() + d));
        goTo(n);
        if (inRail && thumbs[n]) {
          try {
            thumbs[n].focus({ preventScroll: true });
          } catch (e) {}
        }
      });
    }

    /* ------------------------------------------------ full-screen zoom */
    // Tap/click a gallery image: full-screen viewer, swipe/arrows/keys between images,
    // tap/click toggles a 2.5x zoom at that point (finger/trackpad pans; a mouse pans by moving).
    var zoomDlg = qs('[data-sv-zoom-dialog]', root);
    var zoomBtns = qsa('[data-sv-zoom]', root);
    if (zoomDlg && zoomBtns.length && typeof zoomDlg.showModal === 'function') {
      var zTrack = qs('[data-sv-zoom-track]', zoomDlg);
      var zCur = qs('[data-sv-zoom-cur]', zoomDlg);
      var zTotal = qs('[data-sv-zoom-total]', zoomDlg);
      var zPrev = qs('[data-sv-zoom-prev]', zoomDlg);
      var zNext = qs('[data-sv-zoom-next]', zoomDlg);
      var zTip = qs('[data-sv-zoom-tip]', zoomDlg);
      var zSlides = [];
      var zMap = []; // viewer index -> gallery slide index
      var zIdx = 0;
      var zRaf = 0;
      var Z = 2.5;

      if (zTip && W.matchMedia && W.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        zTip.textContent = 'Click to zoom';
      }

      var zBuild = function () {
        if (zSlides.length) return;
        var gs = slides();
        zoomBtns.forEach(function (b) {
          var li = document.createElement('li');
          li.className = 'sv-zoom__slide';
          var pan = document.createElement('div');
          pan.className = 'sv-zoom__pan';
          var img = document.createElement('img');
          img.alt = b.getAttribute('data-zoom-alt') || '';
          img.setAttribute('draggable', 'false');
          img.setAttribute('decoding', 'async');
          img.setAttribute('data-src', b.getAttribute('data-zoom-src') || '');
          pan.appendChild(img);
          li.appendChild(pan);
          zTrack.appendChild(li);
          zSlides.push(li);
          var slide = b.closest ? b.closest('[data-sv-slide]') : null;
          zMap.push(Math.max(0, gs.indexOf(slide)));
        });
        if (zTotal) zTotal.textContent = String(zSlides.length);
      };

      var zLoad = function (i) {
        [i, i + 1, i - 1].forEach(function (k) {
          var li = zSlides[k];
          if (!li) return;
          var img = li.querySelector('img');
          if (img && !img.getAttribute('src')) img.setAttribute('src', img.getAttribute('data-src'));
        });
      };

      var zPaint = function (i) {
        zIdx = i;
        if (zCur) zCur.textContent = String(i + 1);
        if (zPrev) zPrev.disabled = i <= 0;
        if (zNext) zNext.disabled = i >= zSlides.length - 1;
        zLoad(i);
      };

      var zUnzoom = function () {
        zSlides.forEach(function (li) {
          if (!li.classList.contains('is-zoomed')) return;
          li.classList.remove('is-zoomed');
          var pan = li.firstChild;
          var img = pan.querySelector('img');
          if (img) img.style.width = '';
          pan.scrollLeft = 0;
          pan.scrollTop = 0;
        });
        zoomDlg.classList.remove('is-zoomed');
      };

      var zGo = function (i, instant) {
        if (!zSlides.length) return;
        i = Math.max(0, Math.min(zSlides.length - 1, i));
        zUnzoom();
        zPaint(i);
        var left = i * zTrack.clientWidth;
        try {
          zTrack.scrollTo({ left: left, behavior: instant ? 'auto' : behavior() });
        } catch (e) {
          zTrack.scrollLeft = left;
        }
      };

      var zToggle = function (li, ev) {
        var pan = li.firstChild;
        var img = pan.querySelector('img');
        if (!img || !img.complete || !img.naturalWidth) return;
        if (li.classList.contains('is-zoomed')) {
          zUnzoom();
          return;
        }
        // The image box fills the viewer (object-fit: contain): work from the drawn picture inside it.
        var r = img.getBoundingClientRect();
        var fit = Math.min(r.width / img.naturalWidth, r.height / img.naturalHeight);
        var cw = img.naturalWidth * fit;
        var ch = img.naturalHeight * fit;
        var cl = r.left + (r.width - cw) / 2;
        var ct = r.top + (r.height - ch) / 2;
        var hasPoint = ev && typeof ev.clientX === 'number' && (ev.clientX || ev.clientY);
        var cx = hasPoint ? ev.clientX : cl + cw / 2;
        var cy = hasPoint ? ev.clientY : ct + ch / 2;
        var fx = Math.max(0, Math.min(1, (cx - cl) / cw));
        var fy = Math.max(0, Math.min(1, (cy - ct) / ch));
        var w = Math.round(cw * Z);
        li.classList.add('is-zoomed');
        zoomDlg.classList.add('is-zoomed');
        img.style.width = w + 'px';
        var pr = pan.getBoundingClientRect();
        pan.scrollLeft = fx * w - (cx - pr.left);
        pan.scrollTop = fy * ch * Z - (cy - pr.top);
        if (zTip) zTip.hidden = true;
      };

      var zOpen = function (i) {
        zBuild();
        if (!zoomDlg.open) zoomDlg.showModal();
        document.documentElement.classList.add('sv-zoom-open');
        zPaint(i);
        requestAnimationFrame(function () {
          zGo(i, true);
        });
      };

      zoomBtns.forEach(function (b, i) {
        on(b, 'click', function () {
          zOpen(i);
        });
      });

      on(zTrack, 'scroll', function () {
        if (zRaf) return;
        zRaf = requestAnimationFrame(function () {
          zRaf = 0;
          var w = zTrack.clientWidth || 1;
          var i = Math.max(0, Math.min(zSlides.length - 1, Math.round(zTrack.scrollLeft / w)));
          if (i !== zIdx) zPaint(i);
        });
      }, passive);

      on(zTrack, 'click', function (ev) {
        var li = ev.target && ev.target.closest ? ev.target.closest('.sv-zoom__slide') : null;
        if (li) zToggle(li, ev);
      });

      // A mouse pans a zoomed image by moving; touch and trackpads scroll it natively.
      on(zTrack, 'pointermove', function (ev) {
        if (ev.pointerType !== 'mouse') return;
        var li = ev.target && ev.target.closest ? ev.target.closest('.sv-zoom__slide.is-zoomed') : null;
        if (!li) return;
        var pan = li.firstChild;
        var pr = pan.getBoundingClientRect();
        pan.scrollLeft = ((ev.clientX - pr.left) / pr.width) * (pan.scrollWidth - pan.clientWidth);
        pan.scrollTop = ((ev.clientY - pr.top) / pr.height) * (pan.scrollHeight - pan.clientHeight);
      }, passive);

      on(zPrev, 'click', function () {
        zGo(zIdx - 1);
      });
      on(zNext, 'click', function () {
        zGo(zIdx + 1);
      });
      on(qs('[data-sv-zoom-close]', zoomDlg), 'click', function () {
        zoomDlg.close();
      });
      on(zoomDlg, 'keydown', function (ev) {
        if (ev.key === 'ArrowRight') {
          ev.preventDefault();
          zGo(zIdx + 1);
        } else if (ev.key === 'ArrowLeft') {
          ev.preventDefault();
          zGo(zIdx - 1);
        }
      });
      on(zoomDlg, 'close', function () {
        zUnzoom();
        document.documentElement.classList.remove('sv-zoom-open');
        goTo(zMap[zIdx] || 0, true);
      });
    }

    function showMedia(mediaId) {
      if (!track || !mediaId) return;
      var list = slides();
      var idx = -1;
      for (var i = 0; i < list.length; i++) {
        if (list[i].getAttribute('data-media-id') === String(mediaId)) {
          idx = i;
          break;
        }
      }
      if (idx < 0) return;
      goTo(idx);
      // Desktop: the gallery is sticky, but bring it back if the page has moved past the section.
      if (MQ_DESK.matches && gallery) {
        var r = gallery.getBoundingClientRect();
        var vh = W.innerHeight || document.documentElement.clientHeight;
        if (r.bottom < 0 || r.top > vh) {
          try {
            gallery.scrollIntoView({ behavior: behavior(), block: 'start' });
          } catch (e) {
            gallery.scrollIntoView(true);
          }
        }
      }
    }

    /* Shopify-hosted video: autoplay muted while visible; controls appear once the shopper pauses. */
    var videos = qsa('video.sv-pdp__video', root);
    videos.forEach(function (v) {
      v.muted = true;
      v.setAttribute('playsinline', '');
      if (!v.hasAttribute('tabindex')) v.setAttribute('tabindex', '0');
      if (!v.hasAttribute('aria-label')) v.setAttribute('aria-label', 'Product video. Press Enter to pause or play.');
      on(v, 'keydown', function (event) {
        if (v.controls || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        v.click();
      });
      if (MQ_RM.matches) {
        v.removeAttribute('autoplay');
        v.__svUser = true;
        try {
          v.pause();
        } catch (e) {}
        v.controls = true;
      }
      on(v, 'click', function (event) {
        if (v.controls) return;
        event.preventDefault();
        if (v.paused) {
          v.__svUser = false;
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
      on(v, 'pause', function () {
        if (v.__svAuto) {
          v.__svAuto = false;
          return;
        }
        v.__svUser = true;
        v.controls = true;
      });
      on(v, 'play', function () {
        v.__svUser = false;
        v.controls = false;
      });
    });

    if (videos.length && 'IntersectionObserver' in W) {
      var vio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            if (!v.__svUser && v.paused && !MQ_RM.matches) {
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            }
          } else if (!v.paused) {
            v.__svAuto = true;
            v.pause();
          }
        });
      }, { threshold: 0.25 });
      videos.forEach(function (v) {
        vio.observe(v);
      });
      observers.push(vio);
    }

    /* External video / 3D model: poster until requested. */
    qsa('[data-sv-deferred]', root).forEach(function (wrap) {
      var btn = qs('[data-sv-deferred-btn]', wrap);
      var tpl = qs('template[data-sv-deferred-tpl]', wrap);
      if (!btn || !tpl) return;
      on(btn, 'click', function () {
        if (wrap.__svLoaded) return;
        wrap.__svLoaded = true;
        var frag = tpl.content ? tpl.content.cloneNode(true) : null;
        if (!frag) return;
        wrap.appendChild(frag);
        btn.hidden = true;
        var el = qs('iframe, model-viewer, video', wrap);
        if (wrap.getAttribute('data-sv-deferred') === 'model') {
          var mv = qs('model-viewer', wrap);
          if (mv && W.Shopify && typeof W.Shopify.loadFeatures === 'function') {
            // Shopify's own model-viewer UI stylesheet, requested only when a shopper opens a 3D model
            // (the same file Horizon uses; skipped when Horizon already added it).
            if (!document.getElementById('ModelViewerStyle') && !document.getElementById('SvModelViewerStyle')) {
              var css = document.createElement('link');
              css.id = 'SvModelViewerStyle';
              css.rel = 'stylesheet';
              css.href = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
              document.head.appendChild(css);
            }
            W.Shopify.loadFeatures([
              {
                name: 'model-viewer-ui',
                version: '1.0',
                onLoad: function (errors) {
                  if (errors) return;
                  // Safari can define Shopify.ModelViewerUI a beat after onLoad (Horizon polls too).
                  var tries = 0;
                  var attach = function () {
                    if (wrap.__svModelUi || !mv.isConnected) return;
                    if (W.Shopify.ModelViewerUI) {
                      try {
                        wrap.__svModelUi = new W.Shopify.ModelViewerUI(mv);
                      } catch (e) {}
                      return;
                    }
                    if (++tries < 10) later(attach, 50);
                  };
                  attach();
                },
              },
            ]);
          }
        }
        if (el && typeof el.focus === 'function') {
          el.setAttribute('tabindex', el.getAttribute('tabindex') || '0');
          try {
            el.focus({ preventScroll: true });
          } catch (e) {}
        }
      });
    });

    /* ------------------------------------------------------- accordions */
    var accs = qsa('[data-sv-acc]', root);
    accs.forEach(function (d) {
      var sum = qs('summary', d);
      var body = qs('[data-sv-acc-body]', d);
      if (!sum || !body) return;
      on(sum, 'click', function (event) {
        if (MQ_RM.matches || typeof body.animate !== 'function') return; // native toggle
        event.preventDefault();
        if (d.__svAnim) return;
        var easing = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
        var anim;
        if (d.open) {
          var h = body.offsetHeight;
          d.classList.add('is-closing');
          anim = body.animate(
            [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
            { duration: 260, easing: easing }
          );
          anim.onfinish = function () {
            d.open = false;
            d.classList.remove('is-closing');
            d.__svAnim = null;
          };
        } else {
          d.open = true;
          var h2 = body.offsetHeight;
          anim = body.animate(
            [{ height: '0px', opacity: 0 }, { height: h2 + 'px', opacity: 1 }],
            { duration: 320, easing: easing }
          );
          anim.onfinish = function () {
            d.__svAnim = null;
          };
        }
        anim.oncancel = function () {
          d.classList.remove('is-closing');
          d.__svAnim = null;
        };
        d.__svAnim = anim;
      });
    });

    function openAccordion(d, scroll) {
      if (!d) return;
      d.open = true;
      if (scroll) {
        try {
          d.scrollIntoView({ behavior: behavior(), block: 'center' });
        } catch (e) {
          d.scrollIntoView();
        }
        var s = qs('summary', d);
        if (s) {
          try {
            s.focus({ preventScroll: true });
          } catch (e) {}
        }
      }
    }

    // "Size guide" links appear only when a sizing accordion is rendered.
    var sizingAcc = qs('[data-sv-acc][data-sv-acc-source="sizing"]', root);
    qsa('[data-sv-sizeguide]', root).forEach(function (b) {
      if (!sizingAcc) return;
      b.hidden = false;
      on(b, 'click', function () {
        openAccordion(sizingAcc, true);
      });
    });

    /* -------------------------------------------------- delivery estimate */
    var delivery = qs('[data-sv-delivery]', root);
    var deliveryOk = false;
    (function () {
      var cfg = (root.getAttribute('data-delivery') || '').split(',');
      var range = delivery && qs('[data-sv-delivery-range]', delivery);
      if (!delivery || !range || cfg.length !== 4) return;
      var hmin = toInt(cfg[0], 1);
      var hmax = Math.max(hmin, toInt(cfg[1], hmin));
      var tmin = toInt(cfg[2], 2);
      var tmax = Math.max(tmin, toInt(cfg[3], tmin));
      var today = new Date();
      var a = addBusinessDays(today, hmin + tmin);
      var b = addBusinessDays(today, hmax + tmax);
      var ta = fmtDate(a);
      var tb = fmtDate(b);
      range.textContent = ta === tb ? ta : ta + ' ' + String.fromCharCode(0x2013) + ' ' + tb;
      delivery.classList.add('is-ready');
      deliveryOk = true;
    })();

    function paintDelivery(available) {
      if (delivery) delivery.hidden = !(deliveryOk && available);
    }

    /* ------------------------------------------------ fold guard (desktop)
       The stage is sized by CSS; the buy box must also end (delivery line, else the buy buttons)
       inside the first screen. When it would not, drop optional lines step by step:
       .is-fit-1 (benefits, 1-line hook, tighter rhythm), then .is-fit-2 (compact controls). */
    var fold = qs('[data-sv-fold]', root);
    var fitRaf = 0;

    function scrollerTop() {
      var y = W.scrollY || W.pageYOffset || 0;
      var pw = document.querySelector('.page-wrapper');
      if (pw && pw.scrollTop > y) y = pw.scrollTop;
      return y;
    }

    function foldEnd() {
      var d = qs('[data-sv-delivery]', root);
      if (d && !d.hidden && d.getClientRects().length) return d;
      return qs('.sv-pdp__buy', root);
    }

    function measureFit() {
      fitRaf = 0;
      if (!fold) return;
      root.classList.remove('is-fit-1', 'is-fit-2');
      if (!MQ_DESK.matches) return;
      var end = foldEnd();
      if (!end) return;
      var vh = W.innerHeight || document.documentElement.clientHeight;
      var top = scrollerTop();
      var level = 0;
      while (level < 2 && end.getBoundingClientRect().bottom + top > vh - 12) {
        level += 1;
        root.classList.add('is-fit-' + level);
      }
      root.setAttribute('data-sv-fit', String(level));
    }

    function queueFit() {
      if (!fitRaf) fitRaf = requestAnimationFrame(measureFit);
    }

    if (fold) {
      var dynBox = qs('[data-sv-dyn]', root);
      if (dynBox && 'ResizeObserver' in W) {
        // Accelerated checkout renders late and may add a "More payment options" line.
        var dro = new ResizeObserver(queueFit);
        dro.observe(dynBox);
        observers.push(dro);
      }
      if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
        document.fonts.ready.then(function () {
          if (root.__svPdp === api) queueFit();
        });
      }
    }

    on(W, 'resize', function () {
      queueFit();
      paintRailEdges();
      if (!scrollRaf) scrollRaf = requestAnimationFrame(function () {
        scrollRaf = 0;
        lockIdx = -1;
        paintAt(currentSlide(), true);
      });
    }, passive);
    onMq(MQ_DESK, function () {
      queueFit();
      lockIdx = -1;
      paintAt(currentSlide(), true);
      paintRailEdges();
    }, sig);

    /* ======================================================== purchase */
    var form = qs('form[data-sv-form]', root);
    var api = {
      destroy: destroy,
      openBlock: function (el) {
        var d = el && el.closest ? el.closest('[data-sv-acc]') : null;
        if (d && root.contains(d)) d.open = true;
      },
    };

    if (product && product.variants && product.variants.length && form) {
      setupPurchase();
    } else {
      paintDelivery(true); // preview / placeholder mode
      // No variant data: the native form posts id + quantity only, so per-unit picks cannot apply.
      qsa('[data-sv-mix]', root).forEach(function (el) {
        el.hidden = true;
      });
    }

    root.__svPdp = api;

    function setupPurchase() {
      var variants = product.variants;
      var optionSets = qsa('[data-sv-option]', root).sort(function (a, b) {
        return toInt(a.getAttribute('data-sv-option'), 0) - toInt(b.getAttribute('data-sv-option'), 0);
      });
      var idField = qs('[data-sv-id]', form);
      var qtyField = qs('[data-sv-qty]', form);
      var tierEls = qsa('[data-sv-tier]', root);
      var mixEl = qs('[data-sv-mix]', root);
      var mixList = mixEl && qs('[data-sv-mix-list]', mixEl);
      var mixStatus = mixEl && qs('[data-sv-mix-status]', mixEl);
      var priceEl = qs('[data-sv-price]', root);
      var unitEl = qs('[data-sv-unit]', root);
      var atc = qs('[data-sv-atc]', form);
      var atcLabel = atc && qs('[data-sv-atc-label]', atc);
      var errorEl = qs('[data-sv-error]', root);
      var dyn = qs('[data-sv-dyn]', root);
      var sticky = qs('[data-sv-sticky]', root);
      var stickyBtn = sticky && qs('[data-sv-sticky-btn]', sticky);
      var stickyLabel = sticky && qs('[data-sv-sticky-label]', sticky);
      var stickySub = sticky && qs('[data-sv-sticky-sub]', sticky);
      var stickyImg = sticky && qs('.sv-pdp__sticky-img', sticky);
      var unitLabel = root.getAttribute('data-unit-label') || 'Item';
      var cartUrl = root.getAttribute('data-cart-url') || '/cart';
      var cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add';
      var busy = false;
      var addedTimer = 0;
      var mixPicks = [];
      var paintedQty = 0;

      function byId(id) {
        id = Number(id);
        for (var i = 0; i < variants.length; i++) if (variants[i].id === id) return variants[i];
        return null;
      }

      var variant = byId(idField && idField.value) || variants[0];

      /* ---------- options ---------- */
      function radiosOf(fs) {
        return qsa('input[type="radio"]', fs);
      }

      function selectedOptions() {
        return optionSets.map(function (fs, i) {
          var checked = qs('input[type="radio"]:checked', fs);
          if (checked) return checked.value;
          return variant && variant.options ? variant.options[i] : null;
        });
      }

      function exactMatch(sel) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var ok = true;
          for (var j = 0; j < sel.length; j++) {
            if (v.options[j] !== sel[j]) {
              ok = false;
              break;
            }
          }
          if (ok) return v;
        }
        return null;
      }

      // Closest variant that keeps the value just chosen; prefers available, then earlier options.
      function bestMatch(pos, value, sel) {
        var best = null;
        var bestScore = -1;
        variants.forEach(function (v) {
          if (v.options[pos] !== value) return;
          var score = v.available ? 1000 : 0;
          for (var j = 0; j < sel.length; j++) {
            if (j !== pos && v.options[j] === sel[j]) score += sel.length - j;
          }
          if (score > bestScore) {
            bestScore = score;
            best = v;
          }
        });
        return best;
      }

      function syncRadios(opts) {
        optionSets.forEach(function (fs, i) {
          radiosOf(fs).forEach(function (r) {
            r.checked = r.value === opts[i];
          });
        });
      }

      function paintOptions() {
        var sel = selectedOptions();
        optionSets.forEach(function (fs, i) {
          var selEl = qs('[data-sv-opt-sel]', fs);
          if (selEl) selEl.textContent = sel[i] || '';
          radiosOf(fs).forEach(function (r) {
            var val = r.value;
            var avail = false;
            for (var k = 0; k < variants.length; k++) {
              var v = variants[k];
              if (v.options[i] !== val || !v.available) continue;
              var ok = true;
              for (var j = 0; j < sel.length; j++) {
                if (j !== i && v.options[j] !== sel[j]) {
                  ok = false;
                  break;
                }
              }
              if (ok) {
                avail = true;
                break;
              }
            }
            var label = r.nextElementSibling;
            if (!label) return;
            label.classList.toggle('is-na', !avail);
            var na = qs('[data-sv-na]', label);
            if (na) na.hidden = avail;
          });
        });
      }

      /* ---------- tiers ---------- */
      function tierOf(el) {
        var total = el.getAttribute('data-total-cents');
        return {
          el: el,
          qty: Math.max(1, toInt(el.getAttribute('data-qty'), 1)),
          total: total ? toInt(total, null) : null,
          input: qs('[data-sv-tier-input]', el),
        };
      }

      var tiers = tierEls.map(tierOf);

      // Same rule as the Liquid: a tier total never shows above qty x price (the cart would charge less).
      function tierCalc(t, price) {
        var base = t.qty * price;
        var total = t.total !== null && t.total > 0 ? Math.min(t.total, base) : base;
        return { base: base, total: total, save: base - total, each: Math.round(total / t.qty) };
      }

      function selectedTier() {
        for (var i = 0; i < tiers.length; i++) if (tiers[i].input && tiers[i].input.checked) return tiers[i];
        return tiers.length ? tiers[0] : null;
      }

      function paintTiers(initial) {
        if (!tiers.length || !variant || initial) return;
        tiers.forEach(function (t) {
          var c = tierCalc(t, variant.price);
          var totalEl = qs('[data-sv-tier-total]', t.el);
          var eachEl = qs('[data-sv-tier-each]', t.el);
          var saveEl = qs('[data-sv-tier-save]', t.el);
          if (totalEl) totalEl.textContent = money(c.total);
          if (eachEl) {
            eachEl.hidden = t.qty < 2;
            eachEl.textContent = money(c.each) + ' each';
          }
          if (saveEl) {
            saveEl.hidden = c.save <= 0;
            saveEl.textContent = c.save > 0 ? 'Save ' + money(c.save) : '';
          }
        });
      }

      function quantity() {
        var t = selectedTier();
        if (t) return t.qty;
        var q = qtyField ? toInt(qtyField.value, 1) : 1;
        return Math.max(1, q);
      }

      function totalCents() {
        if (!variant) return 0;
        var t = selectedTier();
        if (t) return tierCalc(t, variant.price).total;
        return variant.price * quantity();
      }

      /* ---------- mix & match ---------- */
      function availableVariants() {
        return variants.filter(function (v) {
          return v.available;
        });
      }

      // Only while the selection itself can be bought (a sold-out pick disables the button).
      function mixActive() {
        var t = selectedTier();
        return !!(mixEl && mixList && variant && variant.available && t && t.qty > 1 && variants.length > 1 && availableVariants().length);
      }

      // The selects are the source of truth: a Back navigation without bfcache restores their values
      // (after load, without change events), and what the shopper sees is what must be added.
      function syncPicks() {
        if (!mixList) return false;
        var changed = false;
        qsa('select[data-sv-mix-select]', mixList).forEach(function (s) {
          var i = toInt(s.getAttribute('data-sv-mix-select'), -1);
          var v = Number(s.value);
          if (i < 0 || !v) return;
          if (mixPicks[i] !== v) changed = true;
          mixPicks[i] = v;
        });
        return changed;
      }

      // Buy it now posts the form's id + quantity only, so it cannot honour a mixed selection.
      function mixDiffers() {
        syncPicks();
        if (!variant || !mixActive()) return false;
        for (var i = 0; i < mixPicks.length; i++) if (mixPicks[i] && mixPicks[i] !== variant.id) return true;
        return false;
      }

      // Picks priced differently from the selected variant: tier totals (and therefore the button amount)
      // assume every unit costs the selected variant's price, so no amount is shown; the bag has the real total.
      function mixPriceDiffers() {
        syncPicks();
        if (!variant || !mixActive()) return false;
        for (var i = 0; i < mixPicks.length; i++) {
          var pv = mixPicks[i] ? byId(mixPicks[i]) : null;
          if (pv && pv.price !== variant.price) return true;
        }
        return false;
      }

      // Summary line of the collapsed mix & match box: are all units the same option?
      function paintMixStatus() {
        if (!mixStatus || !mixActive()) return;
        var seen = {};
        var n = 0;
        mixPicks.forEach(function (id) {
          if (id && !seen[id]) {
            seen[id] = true;
            n += 1;
          }
        });
        mixStatus.textContent = n > 1 ? 'Mixed' : 'Same for all';
      }

      function defaultMixId() {
        if (variant && variant.available) return variant.id;
        var av = availableVariants();
        return av.length ? av[0].id : null;
      }

      function buildMix(resetToVariant) {
        if (!mixEl || !mixList) return;
        if (!mixActive()) {
          mixEl.hidden = true;
          mixList.textContent = '';
          return;
        }
        var qty = selectedTier().qty;
        var av = availableVariants();
        var def = defaultMixId();
        if (resetToVariant) mixPicks = [];
        else syncPicks();
        var picks = [];
        for (var n = 0; n < qty; n++) {
          var keep = mixPicks[n];
          var valid = keep && av.some(function (v) {
            return v.id === keep;
          });
          picks.push(valid ? keep : def);
        }
        mixPicks = picks;

        var sid = root.getAttribute('data-section-id') || 'pdp';
        var frag = document.createDocumentFragment();
        picks.forEach(function (pick, n) {
          var item = document.createElement('div');
          item.className = 'sv-pdp__mix-item';
          var id = 'SvMix-' + sid + '-' + (n + 1);
          var label = document.createElement('label');
          label.className = 'sv-pdp__mix-label';
          label.setAttribute('for', id);
          label.textContent = unitLabel + ' ' + (n + 1);
          var wrap = document.createElement('span');
          wrap.className = 'sv-pdp__select';
          var select = document.createElement('select');
          select.className = 'sv-pdp__select-el';
          select.id = id;
          select.setAttribute('data-sv-mix-select', String(n));
          av.forEach(function (v) {
            var o = document.createElement('option');
            o.value = String(v.id);
            o.textContent = v.title;
            if (v.id === pick) o.selected = true;
            select.appendChild(o);
          });
          wrap.appendChild(select);
          item.appendChild(label);
          item.appendChild(wrap);
          frag.appendChild(item);
        });
        mixList.textContent = '';
        mixList.appendChild(frag);
        mixEl.hidden = false;
      }

      if (mixList) {
        on(mixList, 'change', function (event) {
          var s = event.target;
          if (!s || !s.hasAttribute('data-sv-mix-select')) return;
          mixPicks[toInt(s.getAttribute('data-sv-mix-select'), 0)] = Number(s.value);
          clearError();
          paintBuy();
        });
      }

      function lineItems() {
        syncPicks();
        if (mixActive() && mixPicks.length) {
          var agg = {};
          var order = [];
          mixPicks.forEach(function (id) {
            if (!id) return;
            if (!agg[id]) {
              agg[id] = 0;
              order.push(id);
            }
            agg[id] += 1;
          });
          if (order.length) {
            return order.map(function (id) {
              return { id: Number(id), quantity: agg[id] };
            });
          }
        }
        return [{ id: variant.id, quantity: quantity() }];
      }

      /* ---------- paint ---------- */
      function state() {
        if (!variant) return 'unavailable';
        if (!variant.available) return 'soldout';
        return 'ready';
      }

      function paintPrice(initial) {
        if (!variant || initial) return; // first paint: keep Liquid's presentment-currency text
        if (priceEl) priceEl.textContent = money(variant.price);
        if (unitEl) {
          var m = variant.unit_price_measurement;
          if (m && typeof variant.unit_price === 'number') {
            var ref = m.reference_value && m.reference_value !== 1 ? m.reference_value : '';
            unitEl.textContent = money(variant.unit_price) + '/' + ref + (m.reference_unit || '');
            unitEl.hidden = false;
          } else {
            unitEl.hidden = true;
          }
        }
      }

      function paintBuy(initial) {
        var st = state();
        var total = totalCents();
        var amount = mixPriceDiffers() ? '' : money(total);
        var text = st === 'ready' ? 'Add to bag' + (amount ? ' ' + String.fromCharCode(0xb7) + ' ' + amount : '') : st === 'soldout' ? 'Sold out' : 'Unavailable';
        if (atc) {
          atc.disabled = st !== 'ready';
          if (atcLabel && !initial) atcLabel.textContent = text;
        }
        if (dyn) dyn.hidden = st !== 'ready' || mixDiffers();
        paintMixStatus();
        if (qtyField && tiers.length) qtyField.value = String(quantity());
        paintedQty = quantity();
        if (stickyBtn) {
          stickyBtn.disabled = st !== 'ready';
          if (stickyLabel) stickyLabel.textContent = st === 'ready' ? 'Add to bag' : st === 'soldout' ? 'Sold out' : 'Unavailable';
        }
        if (stickySub) {
          var t = selectedTier();
          var lbl = t ? qs('[data-sv-tier-label]', t.el) : null;
          var tierTxt = lbl && tiers.length > 1 ? lbl.textContent.trim() : '';
          var prefix = tierTxt ? tierTxt + ' ' + String.fromCharCode(0xb7) + ' ' : '';
          if (!t && quantity() > 1) prefix = quantity() + ' ' + String.fromCharCode(0xd7) + ' ';
          // First paint keeps Liquid's amount; once JS has painted this node (re-init) it owns the text.
          if (initial && !stickySub.__svPainted) stickySub.textContent = prefix + stickySub.textContent.trim();
          else if (!variant) stickySub.textContent = '';
          else stickySub.textContent = amount ? prefix + amount : tierTxt;
          stickySub.__svPainted = true;
        }
        paintDelivery(st === 'ready');
      }

      // Variant image, else the product's featured image (never a previous variant's photo).
      function paintStickyImage() {
        if (!stickyImg || !variant) return;
        var src = variant.featured_image && variant.featured_image.src;
        if (!src) src = typeof product.featured_image === 'string' ? product.featured_image : '';
        if (!src) return;
        src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160&height=160&crop=center';
        if (stickyImg.getAttribute('src') !== src) {
          stickyImg.removeAttribute('srcset');
          stickyImg.setAttribute('src', src);
        }
      }

      function syncUrl() {
        if (!variant || !root.hasAttribute('data-url-sync') || !W.history || typeof history.replaceState !== 'function') return;
        try {
          var u = new URL(W.location.href);
          if (u.searchParams.get('variant') === String(variant.id)) return;
          u.searchParams.set('variant', String(variant.id));
          history.replaceState(history.state, '', u.toString());
        } catch (e) {}
      }

      // force: repaint every label from data even without a user action (restored form state).
      function setVariant(v, fromUser, force) {
        var changed = v !== variant;
        variant = v;
        clearError();
        if (v && idField) idField.value = String(v.id);
        var initial = !force && !fromUser && !changed;
        paintOptions();
        paintPrice(initial);
        paintTiers(initial);
        if (changed || !fromUser) buildMix(true);
        paintBuy(initial);
        if (force) paintStickyImage();
        if (!fromUser || !v) return;
        paintStickyImage();
        if (v.featured_media && v.featured_media.id) showMedia(v.featured_media.id);
        syncUrl();
        try {
          document.dispatchEvent(
            new CustomEvent('sv:variant:change', {
              detail: { sectionId: root.getAttribute('data-section-id'), variant: v },
            })
          );
        } catch (e) {}
      }

      optionSets.forEach(function (fs, pos) {
        on(fs, 'change', function (event) {
          var r = event.target;
          if (!r || r.type !== 'radio') return;
          var sel = selectedOptions();
          var v = exactMatch(sel);
          if (!v) {
            v = bestMatch(pos, r.value, sel);
            if (v) syncRadios(v.options);
          }
          setVariant(v, true);
        });
      });

      // No-JS select can still be focused by assistive tech in odd setups; keep it authoritative too.
      if (idField && idField.tagName === 'SELECT') {
        on(idField, 'change', function () {
          var v = byId(idField.value);
          if (v) {
            syncRadios(v.options);
            setVariant(v, true);
          }
        });
      }

      tiers.forEach(function (t) {
        if (!t.input) return;
        on(t.input, 'change', function () {
          clearError();
          buildMix(false);
          paintBuy();
        });
      });

      /* ---------- stepper ---------- */
      if (qtyField && !tiers.length) {
        var qMin = Math.max(1, toInt(qtyField.getAttribute('min'), 1));
        var qMax = Math.max(qMin, toInt(qtyField.getAttribute('max'), 99));
        var setQty = function (n) {
          n = Math.min(qMax, Math.max(qMin, toInt(n, qMin)));
          qtyField.value = String(n);
          paintBuy();
        };
        qsa('[data-sv-step]', form).forEach(function (b) {
          on(b, 'click', function () {
            clearError();
            setQty(toInt(qtyField.value, qMin) + toInt(b.getAttribute('data-sv-step'), 0));
          });
        });
        on(qtyField, 'change', function () {
          setQty(qtyField.value);
        });
        on(qtyField, 'input', function () {
          if (qtyField.value !== '') paintBuy();
        });
      }

      /* ---------- add to bag ---------- */
      function clearError() {
        if (errorEl && errorEl.textContent) errorEl.textContent = '';
      }

      function showError(err) {
        var msg = '';
        // Shopify's 422 JSON (and sv-cart.js) carry shopper-facing copy; raw JS/network errors do not.
        if (err && !(err instanceof Error)) {
          if (typeof err.description === 'string' && err.description) msg = err.description;
          else if (typeof err.message === 'string' && err.message && err.message !== 'Cart Error') msg = err.message;
          else if (typeof err === 'string') msg = err;
        }
        if (!msg) msg = 'We couldn' + String.fromCharCode(0x2019) + 't add this to your bag. Please try again.';
        if (errorEl) errorEl.textContent = msg;
      }

      function buttons() {
        return [atc, stickyBtn].filter(Boolean);
      }

      function setBusy(on_) {
        busy = on_;
        buttons().forEach(function (b) {
          b.classList.toggle('is-loading', on_);
          if (on_) b.setAttribute('aria-busy', 'true');
          else b.removeAttribute('aria-busy');
        });
      }

      function flashAdded() {
        clearTimeout(addedTimer);
        buttons().forEach(function (b) {
          b.classList.add('is-added');
        });
        addedTimer = later(function () {
          buttons().forEach(function (b) {
            b.classList.remove('is-added');
          });
        }, ADDED_MS);
      }

      function fallbackAdd(items) {
        return fetch(cartAddUrl.replace(/\/?$/, '') + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        }).then(function (res) {
          return res.json().then(
            function (data) {
              if (!res.ok || (data && data.status && data.status >= 400)) throw data || {};
              W.location.href = cartUrl;
              return data;
            },
            function () {
              if (!res.ok) throw {};
              W.location.href = cartUrl;
            }
          );
        });
      }

      on(form, 'submit', function (event) {
        if (!variant || !variant.available) {
          event.preventDefault();
          return;
        }
        if (busy) {
          event.preventDefault();
          return;
        }
        var fromSticky = !!(event.submitter && event.submitter === stickyBtn);
        var items = lineItems();
        if (idField) idField.value = String(variant.id);
        if (qtyField) qtyField.value = String(quantity());
        clearError();

        var cartApi = S.cart && typeof S.cart.add === 'function' ? S.cart : null;
        if (!cartApi && items.length === 1 && items[0].id === variant.id) {
          setBusy(true); // native POST /cart/add -> Shopify redirects to the cart
          return;
        }

        event.preventDefault();
        setBusy(true);
        var p;
        try {
          p = cartApi ? cartApi.add(items, { open: true }) : fallbackAdd(items);
        } catch (err) {
          p = Promise.reject(err);
        }
        Promise.resolve(p).then(
          function () {
            setBusy(false);
            flashAdded();
          },
          function (err) {
            setBusy(false);
            showError(err);
            if (fromSticky && atc) {
              try {
                atc.scrollIntoView({ behavior: behavior(), block: 'center' });
              } catch (e) {}
            }
          }
        );
      }, sig);

      // Variant from the controls themselves (option radios first, then the id select).
      function domVariant() {
        if (optionSets.length) {
          var m = exactMatch(selectedOptions());
          if (m) return m;
        }
        return (idField && byId(idField.value)) || variant;
      }

      // Liquid-rendered defaults vs the live controls (restored by the browser, or changed before a re-init).
      function controlsDirty() {
        return qsa('input[type="radio"], input[type="number"], select', root).some(function (el) {
          if (el.hasAttribute('data-sv-mix-select')) return false;
          if (el.type === 'radio') return el.checked !== el.defaultChecked;
          if (el.type === 'number') return el.value !== el.defaultValue;
          return qsa('option', el).some(function (o) {
            return o.selected !== o.defaultSelected;
          });
        });
      }

      on(W, 'pageshow', function (event) {
        if (event.persisted) {
          setBusy(false);
          buttons().forEach(function (b) {
            b.classList.remove('is-added');
          });
        }
        // Back/forward without bfcache: the browser restores tier radios / stepper / option radios right
        // before pageshow and fires no change events, so repaint when the controls no longer match.
        var v = domVariant();
        if (v !== variant || quantity() !== paintedQty) setVariant(v, false, true);
        else if (syncPicks()) paintBuy(); // restored mix & match picks: amount + Buy it now follow them
      }, sig);

      /* ---------- sticky mobile bar ----------
         Shown whenever the main button is off screen (below the first screen on load, or scrolled
         past), hidden while it is visible and when the footer arrives. Measured on scroll
         (rAF-throttled) rather than with an IntersectionObserver so fast flings / anchor jumps
         are still caught. */
      if (sticky && atc) {
        var stickyRaf = 0;
        var stickyShown = false;
        var footerNode = null;

        // The page footer: the footer section group first (a <footer> inside a review widget or a
        // closed app dialog must not count), skipping anything that is not rendered.
        var footerEl = function () {
          if (footerNode && footerNode.isConnected && footerNode.getClientRects().length) return footerNode;
          footerNode = null;
          var sels = ['.shopify-section-group-footer-group', '.sv-footer', 'footer'];
          for (var i = 0; i < sels.length && !footerNode; i++) {
            var list = qsa(sels[i]);
            for (var k = 0; k < list.length; k++) {
              if (!root.contains(list[k]) && list[k].getClientRects().length) {
                footerNode = list[k];
                break;
              }
            }
          }
          return footerNode;
        };

        var paintSticky = function () {
          stickyRaf = 0;
          var show = false;
          if (!MQ_DESK.matches) {
            var vh = W.innerHeight || document.documentElement.clientHeight;
            var ar = atc.getBoundingClientRect();
            var atcVisible = ar.bottom > 0 && ar.top < vh;
            var footer = footerEl();
            var footerIn = footer ? footer.getBoundingClientRect().top < vh + 120 : false;
            show = !atcVisible && !footerIn;
          }
          if (show === stickyShown) return;
          stickyShown = show;
          sticky.classList.toggle('is-shown', show);
          if (show) {
            sticky.removeAttribute('inert');
            sticky.removeAttribute('aria-hidden');
          } else {
            if (sticky.contains(document.activeElement)) {
              try {
                atc.focus({ preventScroll: true });
              } catch (e) {}
            }
            sticky.setAttribute('inert', '');
            sticky.setAttribute('aria-hidden', 'true');
          }
        };

        var queueSticky = function () {
          if (!stickyRaf) stickyRaf = requestAnimationFrame(paintSticky);
        };

        on(W, 'scroll', queueSticky, passive);
        on(W, 'resize', queueSticky, passive);
        onMq(MQ_DESK, queueSticky, sig);
        cleanupRaf.push(function () {
          if (stickyRaf) cancelAnimationFrame(stickyRaf);
        });
        queueSticky();
      }

      /* ---------- initial paint ---------- */
      var start = domVariant();
      setVariant(start, false, start !== variant || controlsDirty());
    }

    /* ------------------------------------------------------------ teardown */
    function destroy() {
      if (ac) ac.abort();
      else {
        listeners.forEach(function (l) {
          l[0].removeEventListener(l[1], l[2], l[3]);
        });
      }
      observers.forEach(function (o) {
        try {
          o.disconnect();
        } catch (e) {}
      });
      timers.forEach(clearTimeout);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      if (fitRaf) cancelAnimationFrame(fitRaf);
      root.classList.remove('is-fit-1', 'is-fit-2');
      cleanupRaf.forEach(function (fn) {
        fn();
      });
      delete root.__svPdp;
    }

    paintAt(currentSlide(), true);
    paintRailEdges();
    measureFit(); // after the purchase paint: the buy box has its final first-screen content
  }

  /* ================================================================ boot */
  function boot(ctx) {
    qsa('[data-sv-pdp]', ctx || document).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () {
    boot();
  });
  else boot();

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!event.target) return;
    qsa('[data-sv-pdp]', event.target).forEach(function (r) {
      if (r.__svPdp) r.__svPdp.destroy();
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    var el = event.target;
    var root = el && el.closest ? el.closest('[data-sv-pdp]') : null;
    if (root && root.__svPdp) root.__svPdp.openBlock(el);
  });
})();
